import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { LifecycleState } from '@/types/node';

interface StatusBadgeProps {
  state: 'ALIVE' | 'UNREACHABLE' | string;
  lifecycleState?: LifecycleState;
  className?: string;
}

export function StatusBadge({ state, lifecycleState, className }: StatusBadgeProps) {
  if (lifecycleState === 'new') {
    return (
      <Badge
        variant="secondary"
        className={cn('dls-font-medium dls-bg-status-info/15 dls-text-status-info dls-border-transparent', className)}
      >
        New
      </Badge>
    );
  }

  const isOnline = state === 'ALIVE';

  return (
    <Badge variant={isOnline ? 'success' : 'error'} className={cn('dls-font-medium', className)}>
      {isOnline ? 'Online' : 'Offline'}
    </Badge>
  );
}

interface StatusDotProps {
  state: 'ALIVE' | 'UNREACHABLE' | string;
  lifecycleState?: LifecycleState;
  className?: string;
}

export function StatusDot({ state, lifecycleState, className }: StatusDotProps) {
  if (lifecycleState === 'new') {
    return <span className={cn('dls-inline-block dls-w-2.5 dls-h-2.5 dls-rounded-full dls-bg-status-info', className)} />;
  }

  const isOnline = state === 'ALIVE';

  return (
    <span
      className={cn(
        'dls-inline-block dls-w-2.5 dls-h-2.5 dls-rounded-full',
        isOnline ? 'dls-bg-status-online' : 'dls-bg-status-offline',
        className
      )}
    />
  );
}

/**
 * Service health status badge — colors match ServiceHealthCard categories.
 */
interface ServiceStatusBadgeProps {
  status: string | null | undefined;
  className?: string;
}

function getServiceStatusVariant(status: string | null | undefined): {
  variant: 'success' | 'error' | 'warning' | 'secondary';
  label: string;
  extraClass?: string;
} {
  switch (status?.toUpperCase()) {
    case 'HEALTHY':
      return { variant: 'success', label: 'Healthy' };
    case 'UNHEALTHY':
      return { variant: 'error', label: 'Unhealthy' };
    case 'PENDING':
      return { variant: 'warning', label: 'Pending' };
    case 'TERMINATED':
      return { variant: 'error', label: 'Terminated' };
    case 'RESTARTING':
      return {
        variant: 'warning',
        label: 'Restarting',
        extraClass: 'dls-bg-status-warning-quarternary dls-text-status-warning dls-border-transparent',
      };
    case 'UNKNOWN':
      return {
        variant: 'secondary',
        label: 'Unknown',
        extraClass: 'dls-bg-status-info/15 dls-text-status-info dls-border-status-info',
      };
    default:
      return {
        variant: 'secondary',
        label: status || 'Unknown',
        extraClass: 'dls-bg-neutral-quarternary dls-text-neutral-secondary dls-border-neutral-secondary',
      };
  }
}

export function ServiceStatusBadge({ status, className }: ServiceStatusBadgeProps) {
  const { variant, label, extraClass } = getServiceStatusVariant(status);

  return (
    <Badge variant={variant} className={cn('dls-font-medium', extraClass, className)}>
      {label}
    </Badge>
  );
}
